import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, XCircle, UserCheck, Zap, Loader2, Users } from "lucide-react";
import { API, money, titleCase, postAction, adminPost, fetchDrivers, tryAssignWithOverride } from "./utils";

export function ChevronArrow({ busy }) {
  return busy ? (
    <span className="text-xs text-brand-muted">Assigning…</span>
  ) : (
    <svg className="w-5 h-5 text-brand-muted" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 6l6 6-6 6" /></svg>
  );
}

export function ActionModal({ ride, mode, onClose, onDone, auth }) {
  const isCancel = mode === "cancel";
  const hold = ride.hold_amount_cents || 0;
  const [cents, setCents] = useState(isCancel ? 1500 : hold);
  const [loading, setLoading] = useState(false);
  const dollars = (cents / 100).toFixed(2);

  const submit = async () => {
    setLoading(true);
    try {
      if (isCancel) {
        const data = await postAction(auth, ride.id, "cancel", { fee_cents: Number(cents) });
        toast.success(`Ride cancelled · ${money(data.cancellation_fee_cents)} fee retained`);
      } else {
        const data = await postAction(auth, ride.id, "complete", { amount_cents: Number(cents) });
        toast.success(`Ride completed · ${money(data.captured_amount_cents)} captured`);
      }
      onDone();
    } catch (err) {
      toast.error(err.message || "Action failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-brand-deep/50 backdrop-blur-sm px-4" onClick={onClose}>
      <div className="bg-white rounded-2xl border border-brand-navy/10 shadow-xl w-full max-w-md p-7" onClick={(e) => e.stopPropagation()} data-testid={`${mode}-modal`}>
        <div className="flex items-center gap-3 mb-5">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isCancel ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>
            {isCancel ? <XCircle className="w-5 h-5" /> : <CheckCircle2 className="w-5 h-5" />}
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.22em] font-semibold text-brand-green">
              {isCancel ? "Cancel & capture fee" : "Complete & capture payment"}
            </div>
            <h2 className="font-serif text-2xl text-brand-deep leading-tight">{ride.member_name}</h2>
          </div>
        </div>

        <div className="text-sm text-brand-muted space-y-1 mb-5">
          <div>Trip type: <strong className="text-brand-deep">{titleCase(ride.trip_class)}</strong></div>
          <div>Authorized hold: <strong className="text-brand-deep">{money(hold)}</strong></div>
          <div>Scheduled: {ride.trip_date} · {ride.trip_time}</div>
        </div>

        <label className="block text-xs font-medium text-brand-deep mb-2">
          {isCancel ? "Cancellation fee to retain (default $15, enter 0 to waive)" : "Amount to capture (max = full hold)"}
        </label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted text-sm">$</span>
          <input
            type="number"
            min="0"
            step="0.01"
            value={dollars}
            onChange={(e) => setCents(Math.round(Number(e.target.value) * 100))}
            data-testid={`${mode}-amount-input`}
            className="w-full pl-8 pr-4 py-3 rounded-lg border border-brand-navy/15 focus:outline-none focus:border-brand-green focus:ring-2 focus:ring-brand-green/15 text-brand-deep font-mono"
          />
        </div>
        {!isCancel && hold > 0 && (
          <div className="flex gap-2 mt-3">
            <button type="button" onClick={() => setCents(hold)} className="text-xs px-3 py-1.5 rounded-full border border-brand-navy/15 text-brand-deep hover:border-brand-green hover:text-brand-green transition">
              Full hold {money(hold)}
            </button>
            <button type="button" onClick={() => setCents(Math.round(hold * 0.5))} className="text-xs px-3 py-1.5 rounded-full border border-brand-navy/15 text-brand-deep hover:border-brand-green hover:text-brand-green transition">
              Half
            </button>
          </div>
        )}
        {isCancel && (
          <div className="flex gap-2 mt-3">
            <button type="button" onClick={() => setCents(1500)} className="text-xs px-3 py-1.5 rounded-full border border-brand-navy/15 text-brand-deep hover:border-brand-green hover:text-brand-green transition">$15 default</button>
            <button type="button" onClick={() => setCents(2500)} className="text-xs px-3 py-1.5 rounded-full border border-brand-navy/15 text-brand-deep hover:border-brand-green hover:text-brand-green transition">$25</button>
            <button type="button" onClick={() => setCents(0)} className="text-xs px-3 py-1.5 rounded-full border border-brand-navy/15 text-brand-deep hover:border-brand-green hover:text-brand-green transition">Waive fee</button>
          </div>
        )}

        <div className="mt-6 flex gap-3">
          <button onClick={onClose} className="flex-1 px-4 py-3 rounded-full border border-brand-navy/15 text-brand-deep hover:border-brand-navy transition text-sm font-medium">
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={loading || cents < 0 || (hold && cents > hold)}
            data-testid={`${mode}-confirm-button`}
            className={`flex-1 px-4 py-3 rounded-full text-white text-sm font-medium transition ${isCancel ? "bg-red-600 hover:bg-red-700" : "bg-emerald-600 hover:bg-emerald-700"} disabled:opacity-60`}
          >
            {loading ? "Working…" : isCancel ? `Cancel & keep ${money(cents)}` : `Capture ${money(cents)}`}
          </button>
        </div>
      </div>
    </div>
  );
}

export function SplitModal({ ride, onClose, onDone, auth }) {
  const [n, setN] = useState(2);
  const [busy, setBusy] = useState(false);
  const totalPax = Number(ride.passengers) || 1;
  const base = Math.floor(totalPax / n);
  const rem = totalPax - base * n;
  const perRide = Array.from({ length: n }, (_, i) => Math.max(1, base + (i < rem ? 1 : 0)));
  const submit = async () => {
    setBusy(true);
    try {
      const res = await adminPost(auth, `rides/${ride.id}/split`, { n });
      toast.success(`Split into ${res.ride_ids.length} rides — each ready to assign.`);
      onDone();
    } catch (err) {
      toast.error(err.message || "Split failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="split-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-md p-6">
        <div className="flex items-start justify-between gap-2 mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-brand-green font-semibold">Split ride</div>
            <h3 className="font-serif text-2xl text-brand-deep mt-1">Break into multiple trips</h3>
          </div>
          <button onClick={onClose} className="text-brand-muted hover:text-brand-navy"><XCircle className="w-5 h-5" /></button>
        </div>
        <p className="text-sm text-brand-muted">
          Creates <strong>{n}</strong> separate rides for <strong>{ride.member_name}</strong>. Each keeps the same pickup, destination, date, and time — you can then assign each to a different driver.
        </p>
        <div className="mt-4">
          <label className="text-xs font-medium text-brand-deep block mb-1">How many rides?</label>
          <div className="flex flex-wrap gap-2">
            {[2, 3, 4, 5, 6, 7, 8].map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setN(v)}
                data-testid={`split-n-${v}`}
                className={`px-3 py-2 rounded-full text-sm font-medium border ${
                  n === v
                    ? "bg-brand-navy text-white border-brand-navy"
                    : "bg-white text-brand-deep border-brand-navy/20 hover:border-brand-navy"
                }`}
              >
                {v}
              </button>
            ))}
          </div>
        </div>
        <div className="mt-4 bg-brand-surfaceAlt border border-brand-navy/10 rounded-xl p-3 text-xs text-brand-deep" data-testid="split-preview">
          Passengers per ride: <strong>{perRide.join(" · ")}</strong> {totalPax > 1 ? `(from ${totalPax} total)` : "(1 per ride by default — update on each ride after split)"}
        </div>
        <button
          onClick={submit}
          disabled={busy}
          data-testid="split-confirm"
          className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-brand-navy hover:bg-brand-navyDark text-white font-medium py-3 rounded-full disabled:opacity-60"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" />}
          Split into {n} rides
        </button>
      </div>
    </div>
  );
}

export function AssignModal({ ride, onClose, onDone, auth }) {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const data = await fetchDrivers(auth);
        setDrivers((data.drivers || []).filter((d) => d.active));
      } catch (err) {
        toast.error("Could not load drivers");
      } finally {
        setLoading(false);
      }
    })();
  }, [auth]);

  const assign = async (driverId) => {
    setAssigning(driverId);
    try {
      await tryAssignWithOverride(auth, ride.id, driverId);
      const d = drivers.find((x) => x.id === driverId);
      toast.success(`Ride assigned to ${d?.name || "driver"}`);
      onDone();
    } catch (err) {
      toast.error(err.message || "Assign failed");
    } finally {
      setAssigning("");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-brand-deep/50 backdrop-blur-sm px-4" onClick={onClose}>
      <div className="bg-white rounded-2xl border border-brand-navy/10 shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="assign-modal">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center">
            <UserCheck className="w-5 h-5" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.22em] font-semibold text-brand-green">Assign driver</div>
            <h2 className="font-serif text-xl text-brand-deep leading-tight">{ride.member_name}</h2>
            <div className="text-xs text-brand-muted mt-0.5">{ride.trip_date} · {ride.trip_time}</div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-8 text-sm text-brand-muted">Loading drivers…</div>
        ) : drivers.length === 0 ? (
          <div className="text-center py-8 text-sm text-brand-muted">
            No active drivers yet. Add one in the Drivers tab first.
          </div>
        ) : (
          <div className="space-y-2 max-h-72 overflow-auto">
            {drivers.map((d) => (
              <button
                key={d.id}
                onClick={() => assign(d.id)}
                disabled={assigning !== ""}
                data-testid={`assign-driver-${d.id}`}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl border border-brand-navy/15 hover:border-brand-green hover:bg-emerald-50/30 transition disabled:opacity-60 text-left"
              >
                <div>
                  <div className="font-medium text-brand-deep">{d.name}</div>
                  <div className="text-xs text-brand-muted">{d.phone} · {d.status || "offline"}</div>
                </div>
                <ChevronArrow busy={assigning === d.id} />
              </button>
            ))}
          </div>
        )}
        <button onClick={onClose} className="mt-5 w-full px-4 py-2.5 rounded-full border border-brand-navy/15 text-brand-deep hover:border-brand-navy text-sm font-medium">
          Cancel
        </button>
      </div>
    </div>
  );
}

export function NearestDriversModal({ ride, onClose, onDone, auth }) {
  const [state, setState] = useState({ loading: true, drivers: [], radius: 20, reason: "" });
  const [assigning, setAssigning] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API}/nemt/admin/rides/${ride.id}/nearest-drivers?limit=3`, {
          headers: { "X-Dispatcher-Auth": auth },
        });
        const data = await res.json();
        if (!alive) return;
        if (!res.ok) throw new Error(data?.detail || "Failed to load nearest drivers");
        setState({ loading: false, drivers: data.drivers || [], radius: data.radius_miles, reason: data.reason });
      } catch (err) {
        if (!alive) return;
        toast.error(err.message);
        setState({ loading: false, drivers: [], radius: 20, reason: "error" });
      }
    })();
    return () => { alive = false; };
  }, [ride.id, auth]);

  const assign = async (d) => {
    setAssigning(d.id);
    try {
      await tryAssignWithOverride(auth, ride.id, d.id);
      toast.success(`Assigned to ${d.name} (${d.distance_miles} mi away) — driver notified.`);
      onDone();
    } catch (err) {
      toast.error(err.message || "Assign failed");
    } finally {
      setAssigning("");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-brand-deep/50 backdrop-blur-sm px-4" onClick={onClose}>
      <div className="bg-white rounded-2xl border border-brand-navy/10 shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="nearest-modal">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center">
            <Zap className="w-5 h-5" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.22em] font-semibold text-brand-green">Nearest drivers</div>
            <h2 className="font-serif text-xl text-brand-deep leading-tight">{ride.member_name}</h2>
            <div className="text-xs text-brand-muted mt-0.5">
              {ride.pickup?.slice(0, 60)}{(ride.pickup || "").length > 60 ? "…" : ""}
            </div>
          </div>
        </div>

        {state.loading ? (
          <div className="text-center py-8 text-sm text-brand-muted">Finding drivers…</div>
        ) : state.drivers.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-sm text-brand-muted mb-2">
              {state.reason === "geocode_failed"
                ? "Couldn't locate the pickup address — try 'Pick from list' instead."
                : state.reason === "no_drivers"
                  ? "No available drivers right now."
                  : "No drivers available."}
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="text-xs text-brand-muted mb-1">Top {state.drivers.length} sorted by distance from pickup:</div>
            {state.drivers.map((d, i) => (
              <button
                key={d.id}
                onClick={() => d.within_radius ? assign(d) : (window.confirm(`${d.name} is ${d.distance_miles} mi away — outside the ${state.radius}mi radius. Assign anyway?`) && assign(d))}
                disabled={assigning !== ""}
                data-testid={`nearest-driver-${d.id}`}
                title={d.within_radius ? "Tap to assign" : `Outside ${state.radius}mi radius — extra confirm required`}
                className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl border transition text-left disabled:opacity-60 ${
                  d.within_radius
                    ? "border-brand-green/40 hover:border-brand-green hover:bg-emerald-50/50 bg-white"
                    : "border-amber-400 hover:border-amber-600 hover:bg-amber-50 bg-amber-50/60 opacity-80"
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-serif text-sm ${
                    i === 0 && d.within_radius ? "bg-emerald-600 text-white"
                      : d.within_radius ? "bg-brand-navy/10 text-brand-deep"
                        : "bg-amber-200 text-amber-900"
                  }`}>{i + 1}</div>
                  <div>
                    <div className="font-medium text-brand-deep flex items-center gap-2">
                      {d.name}
                      {!d.within_radius && <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-amber-200 text-amber-900">Far</span>}
                    </div>
                    <div className="text-xs text-brand-muted">
                      {[d.car_year, d.car_color, d.car_make, d.car_model].filter(Boolean).join(" ") || "—"}
                      {d.car_tag ? ` · ${d.car_tag}` : ""}
                    </div>
                    <div className={`text-xs mt-0.5 font-semibold ${d.within_radius ? "text-brand-green" : "text-amber-700"}`}>
                      {d.distance_miles} mi{d.within_radius ? "" : ` · outside ${state.radius}mi radius`}
                    </div>
                  </div>
                </div>
                <ChevronArrow busy={assigning === d.id} />
              </button>
            ))}
          </div>
        )}

        <button onClick={onClose} className="mt-5 w-full px-4 py-2.5 rounded-full border border-brand-navy/15 text-brand-deep hover:border-brand-navy text-sm font-medium">
          Cancel
        </button>
      </div>
    </div>
  );
}
